/* sz-recipe-v2.js — recipe v2 interactions
   Wires up: ServingsScaler, IngredientCheck, VideoModal, TabNav,
   a smooth-scroll fallback for "Start tilberedning" until step 8
   replaces it with full cooking mode, and a Wake Lock-based
   "I køkkenet" toggle that keeps the screen awake while cooking.
   All modules are no-ops if their target elements aren't present. */

(function () {
  'use strict';

  const FRACTION_TO_NUM = {
    '½': 0.5, '¼': 0.25, '¾': 0.75,
    '⅓': 1 / 3, '⅔': 2 / 3,
    '⅕': 0.2, '⅖': 0.4, '⅗': 0.6, '⅘': 0.8,
    '⅙': 1 / 6, '⅛': 0.125
  };

  function parseAmount(str) {
    if (str == null || str === '') return null;
    const trimmed = String(str).trim();
    if (FRACTION_TO_NUM[trimmed] != null) return FRACTION_TO_NUM[trimmed];
    const normalized = trimmed.replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }

  function formatAmount(num) {
    if (!Number.isFinite(num)) return '';
    const rounded = Math.round(num * 100) / 100;
    if (rounded === 0.5) return '½';
    if (rounded === 0.25) return '¼';
    if (rounded === 0.75) return '¾';
    if (Math.abs(rounded - 1 / 3) < 0.01) return '⅓';
    if (Math.abs(rounded - 2 / 3) < 0.01) return '⅔';
    if (Number.isInteger(rounded)) return String(rounded);
    return String(rounded).replace('.', ',');
  }

  function ServingsScaler(root) {
    const valueEl = root.querySelector('[data-servings-value]');
    const decBtn = root.querySelector('[data-servings-decrement]');
    const incBtn = root.querySelector('[data-servings-increment]');
    const list = root.querySelector('[data-ingredients-list]');
    if (!valueEl || !list) return;

    const base = parseInt(list.getAttribute('data-base-servings'), 10) || 4;
    const articleId = root.getAttribute('data-article-id') || 'default';
    const storageKey = `recipe-${articleId}-servings`;
    const MIN = 1;
    const MAX = 20;

    let current = base;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (Number.isFinite(parsed) && parsed >= MIN && parsed <= MAX) current = parsed;
      }
    } catch (_) {}

    function render() {
      const unit = current === 1 ? 'person' : 'personer';
      valueEl.textContent = `${current} ${unit}`;
      if (decBtn) decBtn.disabled = current <= MIN;
      if (incBtn) incBtn.disabled = current >= MAX;

      const rows = list.querySelectorAll('[data-base-amount]');
      rows.forEach((row) => {
        const baseAmount = parseAmount(row.getAttribute('data-base-amount'));
        if (baseAmount == null) return;
        const unitAttr = row.getAttribute('data-unit') || '';
        const scaled = baseAmount * (current / base);
        const display = formatAmount(scaled);
        const amountEl = row.querySelector('[data-ingredient-amount]');
        if (!amountEl) return;
        amountEl.textContent = unitAttr ? `${display} ${unitAttr}` : display;
      });
    }

    function persist() {
      try {
        localStorage.setItem(storageKey, String(current));
      } catch (_) {}
    }

    if (decBtn) {
      decBtn.addEventListener('click', () => {
        if (current > MIN) {
          current -= 1;
          render();
          persist();
        }
      });
    }
    if (incBtn) {
      incBtn.addEventListener('click', () => {
        if (current < MAX) {
          current += 1;
          render();
          persist();
        }
      });
    }

    render();
  }

  function IngredientCheck(root) {
    const rows = root.querySelectorAll('[data-ingredient-row]');
    rows.forEach((row) => {
      function toggle() {
        const next = row.getAttribute('aria-checked') === 'true' ? 'false' : 'true';
        row.setAttribute('aria-checked', next);
      }
      row.addEventListener('click', toggle);
      row.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      });
    });
  }

  function StepCheck(root) {
    const steps = Array.from(root.querySelectorAll('.recipe-v2__step'));
    if (!steps.length) return;

    function updateCurrent() {
      let assigned = false;
      steps.forEach((s) => {
        const isChecked = s.getAttribute('aria-checked') === 'true';
        if (!assigned && !isChecked) {
          s.classList.add('is-current');
          assigned = true;
        } else {
          s.classList.remove('is-current');
        }
      });
    }

    steps.forEach((step) => {
      step.setAttribute('role', 'checkbox');
      step.setAttribute('tabindex', '0');
      if (!step.hasAttribute('aria-checked')) step.setAttribute('aria-checked', 'false');

      function toggle() {
        const next = step.getAttribute('aria-checked') === 'true' ? 'false' : 'true';
        step.setAttribute('aria-checked', next);
        updateCurrent();
      }
      step.addEventListener('click', toggle);
      step.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      });
    });

    updateCurrent();
  }

  function VideoModal() {
    const trigger = document.querySelector('[data-video-trigger]');
    if (!trigger) return;
    const videoUrl = trigger.getAttribute('data-video-url') || '';
    if (!videoUrl) return;

    const SVG_NS = 'http://www.w3.org/2000/svg';

    const modal = document.createElement('div');
    modal.className = 'recipe-v2__video-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Videoguide');
    modal.setAttribute('aria-hidden', 'true');

    const backdrop = document.createElement('div');
    backdrop.className = 'recipe-v2__video-modal-backdrop';
    backdrop.setAttribute('data-modal-close', '');

    const box = document.createElement('div');
    box.className = 'recipe-v2__video-modal-box';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'recipe-v2__video-modal-close';
    closeBtn.setAttribute('data-modal-close', '');
    closeBtn.setAttribute('aria-label', 'Luk video');

    const closeSvg = document.createElementNS(SVG_NS, 'svg');
    closeSvg.setAttribute('width', '18');
    closeSvg.setAttribute('height', '18');
    closeSvg.setAttribute('viewBox', '0 0 24 24');
    closeSvg.setAttribute('fill', 'none');
    closeSvg.setAttribute('stroke', 'currentColor');
    closeSvg.setAttribute('stroke-width', '2');
    closeSvg.setAttribute('stroke-linecap', 'round');
    closeSvg.setAttribute('stroke-linejoin', 'round');
    closeSvg.setAttribute('aria-hidden', 'true');

    const line1 = document.createElementNS(SVG_NS, 'line');
    line1.setAttribute('x1', '6');
    line1.setAttribute('y1', '6');
    line1.setAttribute('x2', '18');
    line1.setAttribute('y2', '18');

    const line2 = document.createElementNS(SVG_NS, 'line');
    line2.setAttribute('x1', '18');
    line2.setAttribute('y1', '6');
    line2.setAttribute('x2', '6');
    line2.setAttribute('y2', '18');

    closeSvg.appendChild(line1);
    closeSvg.appendChild(line2);
    closeBtn.appendChild(closeSvg);

    const video = document.createElement('video');
    video.className = 'recipe-v2__video-modal-video';
    video.controls = true;
    video.playsInline = true;
    video.preload = 'metadata';

    box.appendChild(closeBtn);
    box.appendChild(video);
    modal.appendChild(backdrop);
    modal.appendChild(box);
    document.body.appendChild(modal);

    function open() {
      if (!video.src) video.src = videoUrl;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const tryPlay = video.play();
      if (tryPlay && typeof tryPlay.catch === 'function') tryPlay.catch(() => {});
    }
    function close() {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      video.pause();
    }

    trigger.addEventListener('click', open);
    modal.querySelectorAll('[data-modal-close]').forEach((el) => {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') close();
    });
  }

  function TabNav() {
    const tabs = document.querySelectorAll('.recipe-v2__tab[href^="#"]');
    tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const href = tab.getAttribute('href');
        if (!href || href === '#') return;
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function CookingModeScrollFallback() {
    const btn = document.querySelector('[data-cooking-mode-trigger]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const steps = document.querySelector('#fremgangsmaade');
      if (steps) steps.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ---------- showToast: small shared status primitive ----------
  let toastTimeoutId = null;
  function showToast(message) {
    let toast = document.getElementById('recipe-v2-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'recipe-v2-toast';
      toast.className = 'recipe-v2__toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.setAttribute('data-visible', 'true');
    if (toastTimeoutId != null) clearTimeout(toastTimeoutId);
    toastTimeoutId = setTimeout(() => {
      toast.setAttribute('data-visible', 'false');
    }, 2000);
  }

  // ---------- ShareHandler ----------
  function ShareHandler() {
    const buttons = document.querySelectorAll('[data-share-trigger]');
    if (!buttons.length) return;
    buttons.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const title = btn.getAttribute('data-share-title') || document.title;
        const text = btn.getAttribute('data-share-text') || '';
        const url = btn.getAttribute('data-share-url') || window.location.href;
        const toastMsg = btn.getAttribute('data-share-toast') || 'Link kopieret';
        const payload = { title, text, url };
        if (navigator.share) {
          try {
            await navigator.share(payload);
          } catch (_) {
            // user cancelled — swallow
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          showToast(toastMsg);
        } catch (_) {
          // clipboard blocked — last-resort fallback: select + copy via a hidden textarea
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); showToast(toastMsg); } catch (_) {}
          document.body.removeChild(ta);
        }
      });
    });
  }

  // ---------- PrintHandler ----------
  function PrintHandler() {
    const buttons = document.querySelectorAll('[data-print-trigger]');
    if (!buttons.length) return;
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        window.print();
      });
    });
  }

  // ---------- CookModeTrigger — "I køkkenet" Wake Lock toggle ----------
  // Keeps the screen awake while the user is cooking so the page
  // doesn't dim/sleep. Browser releases the wake lock automatically
  // when the tab is hidden; we re-acquire on visibilitychange.
  function CookModeTrigger() {
    const buttons = document.querySelectorAll('[data-cookmode-trigger]');
    if (!buttons.length) return;

    const supported = 'wakeLock' in navigator;
    let sentinel = null;
    let active = false;

    async function acquire() {
      if (!supported) return false;
      try {
        sentinel = await navigator.wakeLock.request('screen');
        sentinel.addEventListener('release', () => {
          // Browser released it (e.g. tab backgrounded) — drop the reference
          sentinel = null;
        });
        return true;
      } catch (_) {
        sentinel = null;
        return false;
      }
    }

    async function release() {
      if (sentinel) {
        try { await sentinel.release(); } catch (_) {}
        sentinel = null;
      }
    }

    function sync() {
      buttons.forEach((btn) => {
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.classList.toggle('is-active', active);
      });
    }

    async function toggle() {
      if (active) {
        active = false;
        await release();
        sync();
        return;
      }
      if (!supported) {
        showToast('Din browser understøtter ikke at holde skærmen vågen');
        return;
      }
      const ok = await acquire();
      if (!ok) {
        showToast('Kunne ikke holde skærmen vågen');
        return;
      }
      active = true;
      sync();
    }

    // Re-acquire wake lock when the user returns to the tab,
    // as long as the toggle is still on.
    document.addEventListener('visibilitychange', async () => {
      if (active && !sentinel && document.visibilityState === 'visible') {
        await acquire();
      }
    });

    buttons.forEach((btn) => btn.addEventListener('click', toggle));
  }

  function ReviewForm(root) {
    const wrap = root.querySelector('[data-review-form-wrap]');
    const form = root.querySelector('[data-recipe-review-form]');
    if (!wrap || !form) return;

    const articleId = wrap.getAttribute('data-article-id') || 'default';
    const cookieName = `recipe_v2_rated_${articleId}`;

    function hasCookie() {
      return document.cookie.split(';').some((c) => c.trim().startsWith(cookieName + '='));
    }

    function setCookie() {
      try {
        const oneYear = 31536000;
        document.cookie = `${cookieName}=1;max-age=${oneYear};path=/;SameSite=Lax`;
      } catch (_) {}
    }

    const alreadyEl = wrap.querySelector('[data-review-already]');
    const headingEl = wrap.querySelector('[data-review-heading]');

    if (hasCookie()) {
      form.style.display = 'none';
      if (headingEl) headingEl.style.display = 'none';
      const successEl = wrap.querySelector('.recipe-v2__review-success');
      if (alreadyEl && !successEl) alreadyEl.hidden = false;
      return;
    }

    const stars = Array.from(form.querySelectorAll('.recipe-v2__star-input'));
    const bodyInput = form.querySelector('textarea[name="comment[body]"]');
    const honeypot = form.querySelector('[data-honeypot]');
    if (!stars.length || !bodyInput) return;

    let rating = 0;

    function paint(value) {
      stars.forEach((s, i) => s.classList.toggle('is-filled', i < value));
    }

    stars.forEach((star, i) => {
      const value = i + 1;
      star.setAttribute('tabindex', '0');
      star.addEventListener('click', () => {
        rating = value;
        paint(rating);
      });
      star.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          rating = value;
          paint(rating);
        }
      });
      star.addEventListener('mouseenter', () => paint(value));
    });

    const starsContainer = form.querySelector('.recipe-v2__stars-input');
    if (starsContainer) {
      starsContainer.addEventListener('mouseleave', () => paint(rating));
    }

    form.addEventListener('submit', (e) => {
      if (honeypot && honeypot.value) {
        e.preventDefault();
        return;
      }
      if (!rating) {
        e.preventDefault();
        alert('Vælg venligst en vurdering fra 1 til 5 stjerner.');
        stars[0].focus();
        return;
      }
      const userText = (bodyInput.value || '').trim();
      bodyInput.value = `[RATING:${rating}] ${userText}`;
    });
  }

  function init() {
    const root = document.querySelector('[data-recipe-v2]');
    if (!root) return;
    ServingsScaler(root);
    IngredientCheck(root);
    StepCheck(root);
    VideoModal();
    TabNav();
    CookingModeScrollFallback();
    ShareHandler();
    PrintHandler();
    CookModeTrigger();
    ReviewForm(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
